import React from 'react';
import '../styles/main/App.scss';
import ButtonPrimary from '../components/button';
import Contato from '../components/contatoComponent';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faGlobe, faMobileScreen, faGears, faPlugCircleBolt, faChartLine} from '@fortawesome/free-solid-svg-icons';
import {faLinkedin} from '@fortawesome/free-brands-svg-icons';
import {useInView} from 'react-intersection-observer';

import jao from '../assets/equipe/JaoGabriel.jpg';
import leo from '../assets/equipe/leo.jpeg';

import pazuLogo from '../assets/clientes/pazu.png';
import segplaceLogo from '../assets/clientes/segplce.png';
import tempoDeBrincarLogo from '../assets/clientes/tempoDeBrincar.png';

const teamMembers = [
	{src: jao, name: 'João Gabriel', role: 'Software Engineer', linkedin: 'https://www.linkedin.com/in/joaogabrielarruda/'},
	{src: leo, name: 'Leonardo Eberle', role: 'Software Engineer', linkedin: 'https://www.linkedin.com/in/leonardo-eberle'},
];

const services = [
	{icon: faGlobe, title: 'Presença digital', desc: 'Site e app profissional que coloca sua empresa à frente dos concorrentes'},
	{icon: faMobileScreen, title: 'Aplicativos mobile', desc: 'Seu negócio na palma da mão dos seus clientes, a qualquer hora'},
	{icon: faGears, title: 'Automação de processos', desc: 'Elimine tarefas manuais e libere sua equipe para o que realmente importa'},
	{icon: faPlugCircleBolt, title: 'Integração de sistemas', desc: 'Suas ferramentas conversando entre si, sem retrabalho e sem erros'},
	{icon: faChartLine, title: 'Gestão baseada em dados', desc: 'Decisões mais inteligentes com dashboards e relatórios em tempo real'},
];

type Project = {
	id: string;
	title: string;
	description: string;
	tags: string[];
	logo: string;
	bandGradient: string;
	accentColor: string;
	colorClass: string;
	logoClass?: string;
};

const projects: Project[] = [
	{
		id: 'pazu-beachwear',
		title: 'Pazu Beachwear',
		description: 'E-commerce completo para marca brasileira de roupas de banho nos EUA — loja com carrinho e pagamento integrado, mais portal administrativo para gestão de produtos e catálogo.',
		tags: ['E-commerce', 'Portal Administrativo', 'Pagamento Online'],
		logo: pazuLogo,
		bandGradient: 'linear-gradient(135deg, #1A3A5C 0%, #2FAB73 100%)',
		accentColor: '#2FAB73',
		colorClass: 'portfolio__card--green',
	},
	{
		id: 'segplace',
		title: 'Segplace',
		description: 'Presença digital do zero para corretora de seguros brasileira — landing page institucional e sistema administrativo para gestão de clientes e apólices.',
		tags: ['Presença Digital', 'Gestão de Clientes', 'Gestão de Apólices'],
		logo: segplaceLogo,
		bandGradient: 'linear-gradient(135deg, #1C1C3A 0%, #4066E0 100%)',
		accentColor: '#4066E0',
		colorClass: 'portfolio__card--blue',
	},
	{
		id: 'tempo-de-brincar',
		title: 'Tempo de Brincar',
		description: 'Site institucional para empresa de recreação infantil de Curitiba — página profissional com automação de contato para captura e nutrição de leads.',
		tags: ['Presença Digital', 'Captação de Leads', 'Automação de Contato'],
		logo: tempoDeBrincarLogo,
		bandGradient: 'linear-gradient(135deg, #2A1A0E 0%, #E07320 100%)',
		accentColor: '#E07320',
		colorClass: 'portfolio__card--amber',
		logoClass: 'portfolio__card-logo--opaque',
	},
];

function App() {
	const {ref: teamRef, inView: teamInView} = useInView({triggerOnce: true, threshold: 0.15});
	const {ref: servicesRef, inView: servicesInView} = useInView({triggerOnce: true, threshold: 0.1});
	const {ref: portfolioRef, inView: portfolioInView} = useInView({triggerOnce: true, threshold: 0.1});

	return (
		<div className='App'>
			<div className='banner is-flex is-align-items-center'>
				<div className='bd-content'>
					<div className='columns'>
						<div className='column is-three-fifths'></div>
						<div className='column'>
							<h1>
                                A sua <b>visão</b>,<br /> o nosso <b>código</b>.
							</h1>
							<p>
                                Transformamos negócios tradicionais em operações
                                digitais eficientes. Na <b>Capiware</b>, desenvolvemos
                                soluções sob medida que <b>reduzem custos</b>, eliminam
                                processos manuais e colocam sua empresa no digital
                                — pronta para crescer.
							</p>
							<a href='#idContatos'>
								<ButtonPrimary text={'Entre em Contato'} shadow={true} />
							</a>
						</div>
					</div>
				</div>
			</div>

			<div id='idQuemSomos' className='quemSomos'>
				<div className='bd-content'>

					{/* Section header — centered */}
					<div className={`quemSomos__header fade-up ${teamInView ? 'is-visible' : ''}`} ref={teamRef}>
						<span className='quemSomos__eyebrow'>Nossa equipe</span>
						<h2>Quem somos</h2>
						<p className='quemSomos__lead'>
							Somos especialistas em <b>transformação digital</b> para empresas que querem
							parar de perder tempo com processos manuais e começar a crescer de verdade.
							Entregamos soluções que geram resultado — não apenas código.
						</p>
					</div>

					{/* Team cards — large format */}
					<div className='quemSomos__team-grid'>
						{teamMembers.map((member, i) => (
							<div
								key={member.name}
								className={`quemSomos__profile-card fade-up fade-up--delay-${i + 1} ${teamInView ? 'is-visible' : ''}`}
							>
								<div className='quemSomos__profile-img-wrap'>
									<img src={member.src} alt={`Foto de ${member.name}`} />
								</div>
								<div className='quemSomos__profile-info'>
									<span className='name'>{member.name}</span>
									<span className='role'>{member.role}</span>
									{member.linkedin && (
										<a href={member.linkedin} target='_blank' rel='noreferrer' className='linkedin'>
											<FontAwesomeIcon icon={faLinkedin} /> LinkedIn
										</a>
									)}
								</div>
							</div>
						))}
					</div>

					{/* Divider */}
					<div className='quemSomos__divider' />

					{/* Services — 5 column strip */}
					<div
						className={`quemSomos__servicos-grid fade-up ${servicesInView ? 'is-visible' : ''}`}
						ref={servicesRef}
					>
						{services.map(service => (
							<div key={service.title} className='quemSomos__servico'>
								<FontAwesomeIcon icon={service.icon} className='quemSomos__servico-icon' />
								<p className='quemSomos__descricao--item'>{service.title}</p>
								<p className='quemSomos__servico-desc'>{service.desc}</p>
							</div>
						))}
					</div>

				</div>
			</div>

			<div id='idProjetos' className='portfolio'>
				<div className='bd-content'>
					<div className={`portfolio__header fade-up ${portfolioInView ? 'is-visible' : ''}`} ref={portfolioRef}>
						<span className='portfolio__eyebrow'>Projetos</span>
						<h2>Nossos projetos</h2>
					</div>
					<div className='portfolio__grid'>
						{projects.map((project, i) => (
							<div
								key={project.id}
								className={`portfolio__card ${project.colorClass} fade-up fade-up--delay-${i + 1} ${portfolioInView ? 'is-visible' : ''}`}
							>
								<div className='portfolio__card-band' style={{background: project.bandGradient}}>
									<img
										src={project.logo}
										alt={`Logo ${project.title}`}
										className={`portfolio__card-logo${project.logoClass ? ` ${project.logoClass}` : ''}`}
									/>
								</div>
								<div className='portfolio__card-body'>
									<div className='portfolio__card-tags'>
										{project.tags.map(tag => (
											<span key={tag} className='portfolio__tag' style={{color: project.accentColor}}>{tag}</span>
										))}
									</div>
									<p className='portfolio__card-title'>{project.title}</p>
									<p className='portfolio__card-desc'>{project.description}</p>
								</div>
							</div>
						))}
					</div>
					<div className='portfolio__cta'>
						<p>Quer um projeto como esse?</p>
						<a href='#idContatos'>
							<ButtonPrimary text='Entre em contato' shadow={true} />
						</a>
					</div>
				</div>
			</div>

			<div id='idContatos' className='faleConosco'>
				<div className='bd-content is-flex is-flex-wrap-wrap is-justify-content-center'>
					<h2 className='mb-5'>Entre em contato conosco</h2>
					<div className='faleConosco__form is-flex is-justify-content-center'>
						<Contato />
					</div>
				</div>
			</div>
		</div>
	);
}

export default App;
